import React, { useState } from "react";
import { Row, Col, Form, Alert, Card, Modal, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaFileContract,
  FaShieldAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSignature,
  FaEye,
  FaDownload,
} from "react-icons/fa";

const DisclosuresStep = ({
  formData,
  updateFormData,
  errors,
  setErrors,
  nextStep,
  prevStep,
}) => {
  const [showModal, setShowModal] = useState(null);
  const [signature, setSignature] = useState("");

  const disclosures = formData.disclosures || {};

  // Handle checkbox changes
  const handleCheckboxChange = (field, value) => {
    updateFormData("disclosures", {
      ...disclosures,
      [field]: value,
      signatureDate: value ? new Date().toISOString() : null,
    });

    // Clear errors when checkbox is checked
    if (value && errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle signature
  const handleSignature = (name) => {
    if (name.trim().length < 2) {
      setErrors((prev) => ({
        ...prev,
        signature: "Please enter your full name for digital signature",
      }));
      return;
    }

    updateFormData("disclosures", {
      ...disclosures,
      digitalSignature: name.trim(),
      signatureDate: new Date().toISOString(),
    });

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.signature;
      return newErrors;
    });

    setSignature(name);
  };

  // Check if all required disclosures are accepted
  const allRequiredAccepted =
    disclosures.termsAccepted &&
    disclosures.privacyAccepted &&
    disclosures.riskDisclosureAccepted &&
    disclosures.digitalSignature;

  // Document content
  const documents = {
    terms: {
      title: "Terms and Conditions",
      content: `AIRCLIFT CAPITAL TERMS AND CONDITIONS 

INDEX
1. Introduction & Definitions 
2. Account Application & Requirements  
   - 2.1 Personal Accounts  
   - 2.2 Corporate Accounts  
   - 2.3 Account Verification (KYC/AML)  
3. Account Types & Funding 
   - 3.1 Retail Accounts  
   - 3.2 Institutional Accounts  
   - 3.3 Company Trading Equity (CTE) & Bonuses  
4. Trading Terms & Conditions
   - 4.1 Trading Bot & Strategies  
   - 4.2 Cryptocurrency Trading (Scalping)  
   - 4.3 Commodities Trading (Options Contracts)  
   - 4.4 Leverage & Margin Exposure  
   - 4.5 Profit Sharing (30% Company Share)  
5. Deposits & Withdrawals
   - 5.1 Accepted Payment Methods  
   - 5.2 Payout Processing Times  
6. Company Rights & Discretionary Powers 
7. Risk Disclosure & Market Disclaimer
8. Exclusion of Liability
9. Termination & Account Suspension  
10. Amendments to Terms & Conditions 
11. Governing Law & Jurisdiction 

---

1. INTRODUCTION & DEFINITIONS 

Welcome to AirClift Capital ("the Platform," "we," "us," or "our"), a sophisticated cryptocurrency and commodities trading platform. These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("Client," "user," or "trader") and AirClift Capital, governing your access to and use of our proprietary trading technologies, including automated trading bots, scalping strategies, and institutional-grade commodities options trading.  

By registering an account, depositing funds, or engaging in any trading activity, you acknowledge that you have read, understood, and agreed to be bound by these Terms in their entirety. If you do not agree with any provision herein, you must immediately cease using our services.  

Key Definitions:
- "Client" refers to any individual, corporate entity, or institutional trader registered on the Platform.  
- "Trading Bot" denotes our proprietary algorithmic trading system that executes trades autonomously based on predefined strategies.  
- "Scalping" is a high-frequency trading strategy characterized by rapid buy-sell executions to capitalize on minor price fluctuations.  
- "CTE (Company Trading Equity)" represents a promotional bonus credited to a user's account, which may be used for trading but is not redeemable for cash.  

---

2. ACCOUNT APPLICATION & REQUIREMENTS  

2.1 Personal Accounts
To ensure compliance with UK financial regulations and anti-fraud measures, all personal account applicants must meet the following criteria:  
- Be at least 18 years of age (or the legal age of majority in their jurisdiction).  
- Provide a valid government-issued photo ID (passport, national ID card, or driver's license).  
- Submit proof of residence, such as a recent utility bill or bank statement (no older than three months).  
- Only **one account per individual is permitted unless prior written authorization is granted by AirClift Capital.  

We reserve the right to reject applications at our sole discretion, particularly if submitted documentation appears fraudulent or insufficient.  

2.2 Corporate Accounts
Corporate clients must furnish additional documentation to verify the legitimacy of their business operations:  
- Certificate of Incorporation (or equivalent company registration document).  
- Memorandum & Articles of Association (or equivalent constitutional documents).  
- Register of Directors & Ultimate Beneficial Owners (UBOs) (identifying all individuals holding 10%+ ownership).  
- Corporate bank statement (confirming the entity's financial standing).  

Corporate accounts may be subject to enhanced due diligence (EDD) checks, particularly for politically exposed persons (PEPs) or high-risk jurisdictions.  

2.3 Account Verification (KYC/AML) 
AirClift Capital adheres strictly to UK Anti-Money Laundering (AML) and Counter-Terrorist Financing (CTF) regulations. As such:  
- All clients must complete Know Your Customer (KYC) verification before trading.  
- We may request additional documentation (e.g., source of funds declarations) at any time.  
- Failure to comply with verification requests may result in account suspension or termination.  

---

3. ACCOUNT TYPES & FUNDING

3.1 Retail Accounts 
Our retail trading tiers are structured to accommodate varying levels of capital and trading sophistication:  

Tier     
●	Bronze	  $5,000            
●	Silver		$15,000            
●	Gold		$25,000    

(See Account Types in website for features of each account type)        

3.2 Institutional Accounts 
Designed for high-net-worth individuals, hedge funds, and corporate traders:  
- Minimum deposit: $100,000  
- Direct Market Access (DMA) for commodities options.  
- Customizable algorithmic strategies tailored to institutional risk parameters.  
- Reduced Commissions 

(See Account Types in website for features of each account type) 

3.3 Company Trading Equity (CTE) & Bonuses  
AirClift Capital may, at its discretion, offer CTE bonuses to incentivize trading activity:  
- CTE funds are non-withdrawable—only profits generated from them may be withdrawn.  
- Trading volume requirements may apply before bonus profits are released.  
- We reserve the right to modify or revoke bonuses if terms are violated.  

---

4. TRADING TERMS & CONDITIONS

4.1 Trading Bot & Strategies 
Our proprietary trading bot operates autonomously, executing trades based on real-time market data and algorithmic scalping strategies.  
- The bot's performance is not guaranteed—past results do not indicate future returns.  

4.2 Cryptocurrency Trading (Scalping) 
The bot specializes in high-frequency crypto-to-crypto trades, such as:  
- BTC/ETH, SOL/USDT, XRP/BTC 
- Ultra-short holding periods (seconds to minutes).  

4.3 Commodities Trading (Options Contracts)  
We exclusively trade options contracts (no spot/futures), leveraging institutional liquidity pools for:  
- Gold, Oil, Silver, and other major commodities.  
- European-style options (exercisable only at expiry).  

4.4 Leverage & Margin Exposure 
- Leverage is applied to enhance position sizing, but margin calls can only occur if your broker explicitly asks your consent to move forward on an opportunity where such outcome is a possibility.  
- Clients must maintain sufficient equity to avoid forced liquidations, only if participating on margin based investing, which would only happen with prior communication by your broker and you consent of moving forward with said action 

4.5 Profit Sharing (15% Company Share Retail Account - 10% Company Share Institutional Account)  
- AirClift Capital retains a % of net realized profits (% defined by your account type) 
- No profits = no fees—our earnings are directly tied to your success.  

---

5. DEPOSITS & WITHDRAWALS  

5.1 Accepted Payment Methods - Deposits & Withdrawals
- Cryptocurrencies: BTC, ETH, USDT, USDC, and other major coins.  
- Bank Transfers: GBP, USD, EUR (must match account holder name) 

5.2 Withdrawal Policies 

The Collective Capital Model  

At the core of our investment platform lies a proprietary algorithmic trading system (the "Trading Bot") designed to maximize returns through advanced quantitative strategies, including high-frequency trading, arbitrage, and machine learning-driven market predictions. Unlike traditional investment funds, our system operates most efficiently when investor capital remains pooled for extended periods, allowing the bot to:  

1. Optimize Liquidity Utilization – Capital must remain available to seize fleeting market opportunities, such as price discrepancies or sudden volatility spikes, which require immediate deployment of funds.  
2. Reduce Market Impact – Large or premature withdrawals force the bot to unwind positions prematurely, potentially causing slippage and diminishing returns for all participants.  
3. Compound Gains Effectively – Many strategies rely on reinvestment cycles; early withdrawals disrupt compounding efficiency.  
4. Maintain Hedging Integrity – The bot dynamically balances risk exposure across the entire capital pool. Removing funds abruptly may leave remaining investors overexposed.  

To ensure fairness and sustainability, withdrawal restrictions and penalties are implemented to discourage actions that could destabilize the system.  

—

Section 1: Retail Account Withdrawal Policy  
5.1.1 Lock-In Period 
Retail investors agree that, upon account activation, their capital will remain fully committed for a minimum of six (6) consecutive months before becoming eligible for withdrawal. This period allows the Trading Bot to integrate the funds into its broader strategy matrix, ensuring optimal performance without disruptive capital fluctuations. In the same way, AirClift Capital cannot collect their 30% profit-sharing portion, until the (6) months have occurred since the Client´s account activation.

5.1.2 Early Withdrawal Penalty (35% of Total Account Value)
If a retail account holder submits a withdrawal request before the six-month lock-in period has elapsed, the following conditions apply:  

- A penalty of 35% of the account's total value (calculated at the time of the request) will be deducted.  
-AirClift capital would become immediately eligible for their portion of their profit share in the event this occurs
- This penalty is applied regardless of the requested withdrawal amount (partial or full).  
 
Early withdrawals create inefficiencies in the Trading Bot's operations, including:  
- Forced Position Liquidation – The bot may need to close profitable trades prematurely to meet withdrawal demands.  
- Reduced Leverage Capacity – Sudden capital outflows may limit the bot's ability to execute leveraged strategies.  
- Administrative Rebalancing Costs – The platform must recalibrate risk models and liquidity reserves, incurring indirect costs.  

—

Section 2: Institutional Account Withdrawal Policy
5.1.3 Lock-In Period (12 Months Minimum)  
Institutional accounts (including high-net-worth individuals, corporate investors, and fund partners) are subject to a twelve (12)-month lock-in period from the date of activation or institutional upgrade. Just like for the retail accounts, in the Institutional Account, AirClift Capital can't access their portion of the profit sharing, until the (12)-month lock-in period has been finalized. This extended duration is necessary due to:  

Larger Capital Allocations – Institutional deposits often comprise significant portions of the Trading Bot's working capital.  
Complex Strategy Integration – Institutional funds are frequently allocated to longer-term strategies (e.g., arbitrage loops, algorithmic market-making).  
Systemic Risk Mitigation – Sudden large withdrawals could destabilize liquidity across the entire platform.  

5.1.4 Early Withdrawal Penalty (50% of Total Account Value) 
Institutional clients requesting withdrawals before the 12-month term expires will incur:  

-AirClift capital would become immediately eligible for their portion of their profit share in the event this occurs

Institutional withdrawals impose a disproportionate strain on the Trading Bot's ecosystem, including:  
Liquidity Crunch Risks – Large redemptions may force fire sales of assets, harming overall performance.  
Strategy Disruptions – Institutional capital is often tied to multi-phase trades; early removal can leave positions unhedged.  
 
The 50% penalty reflects the greater systemic impact of institutional withdrawals compared to retail.  

- 

—

Section 3: Additional Provisions
5.1.5 Exceptions (Force Majeure & Extraordinary Circumstances)  
In rare cases (e.g., legal insolvency, regulatory action, or platform failure), early withdrawal penalties may be waived or reduced at the sole discretion of the Company. Clients must submit formal documentation for review.  

5.1.6 Post-Lock-In Withdrawals 
After the lock-in period expires:  
- Crypto withdrawals are processed within 24-48 hours
- Bank wire withdrawals are processed within 3 - 5 business days. Due to AML and CTF regulations, the funds would need to go back to the same account that your trading account got funded from   

5.1.7 Transparency & Reporting
To reinforce trust, institutional clients receive:  
Quarterly liquidity audits showing capital deployment.  
Stress-test summaries demonstrating how withdrawals could impact the system.  

---

6. COMPANY RIGHTS & DISCRETIONARY POWERS
We reserve the right to:  
- Modify trading parameters(e.g., leverage, asset availability).  
- Suspend accounts - suspected of fraudulent activity.  
- Adjust CTE bonus terms - at any time.  

---

7. RISK DISCLOSURE & MARKET DISCLAIMER 
- Trading involves l risk—only risk capital should be used.  
- We do not control market movements—losses may occur 

---

8. EXCLUSION OF LIABILITY  

AirClift Capital and its affiliates, directors, employees, and agents shall not, under any circumstances, be held liable for any direct, indirect, incidental, consequential, or punitive damages arising from or related to:  

8.1 Trading & Market Risks 
- Losses incurred due to market volatility, slippage, or liquidity shortages.  
- Unforeseen black swan events (e.g., exchange hacks, regulatory crackdowns, geopolitical crises).  
Inaccurate price feeds, latency issues, or execution delays caused by third-party data providers.  

8.2 Technical & Operational Failures
- System outages, server crashes, or software malfunctions disrupting trading activity.  
- Cyberattacks, hacking incidents, or unauthorized access to client accounts.  
 

8.3 Third-Party Services 
- Banking delays, payment processor errors, or blockchain network congestion affecting withdrawals.  
- **Misconduct by external custodians** (e.g., mismanagement of client assets).  

8.4 Client-Side Issues  
- Unauthorized account access due to weak passwords or phishing attacks.  
- Failure to comply with margin requirements, resulting in forced liquidations.  

8.5 No Guarantee of Performance 
- Historical trading results are not indicative of future returns. 
- The Platform does not guarantee profits or protection against losses. 
- Algorithmic strategies may underperform in certain market conditions.

---

9. TERMINATION & ACCOUNT SUSPENSION
Accounts may be terminated for:  
- Violating these Terms.  
- Suspicious trading activity.  


---

10. AMENDMENTS TO TERMS & CONDITIONS
We may update these Terms periodically. Continued use constitutes acceptance.  

---
 
11. GOVERNING LAW & JURISDICTION

11.1 Applicable Law  
These Terms and Conditions, and any disputes arising from them, shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of laws principles.  

11.2 Exclusive Jurisdiction  
Any legal action, claim, or dispute related to AirClift Capital shall be subject to the exclusive jurisdiction of the courts of England and Wales. Clients irrevocably submit to this jurisdiction and waive any objections based on forum non conveniens or similar doctrines.  

11.3 Arbitration (Optional) 
- For institutional clients or disputes exceeding £100,000, parties may agree to resolve conflicts through binding arbitration under the London Court of International Arbitration (LCIA) rules.  
- Arbitration proceedings shall be confidential and held in London, UK.  
- The arbitrator's decision shall be final and enforceable in any competent court.  

11.4 Waiver of Class Actions
Clients waive any right to participate in class-action lawsuits or consolidated claims against AirClift Capital, agreeing to pursue disputes individually.  

11.5 Regulatory Compliance 
- AirClift Capital operates under **UK financial regulations**, including but not limited to:  -  Financial Conduct Authority (FCA) guidelines (where applicable).  
- Anti-Money Laundering (AML) and Counter-Terrorist Financing (CTF) laws. 
- Non-UK residents remain bound by these Terms but acknowledge that local laws may impose additional restrictions.**  

11.6 Severability 
If any provision of these Terms is deemed invalid or unenforceable, the remaining provisions shall remain in full force and effect

---

By using AirClift Capital, you acknowledge that you have read, understood, and agreed to these Terms and Conditions in full.`,
    },
    privacy: {
      title: "Privacy Policy",
      content: `AIRCLIFT CAPITAL PRIVACY POLICY

1. INFORMATION WE COLLECT

1.1 Personal Information
- Full legal name, date of birth, and contact details
- Government-issued identification documents
- Proof of address and residence verification
- Financial information for KYC/AML compliance
- Employment and income verification documents

1.2 Trading Information
- Trading activity and transaction history
- Account balances and performance data
- Risk tolerance and investment preferences
- Device and IP address information
- Platform usage patterns and preferences

1.3 Technical Information
- Browser type and version
- Operating system and device information
- IP address and geolocation data
- Cookies and tracking technologies
- Log files and analytics data

2. HOW WE USE YOUR INFORMATION

2.1 Identity Verification & Compliance
- KYC/AML regulatory compliance
- Fraud prevention and detection
- Account security and authentication
- Regulatory reporting requirements

2.2 Platform Operations
- Account management and customer support
- Trading execution and settlement
- Risk management and monitoring
- Platform security and maintenance

2.3 Service Improvement
- Analytics and performance optimization
- Product development and enhancement
- Customer experience improvement
- Market research and analysis

2.4 Communication
- Account notifications and updates
- Marketing communications (with consent)
- Legal and regulatory communications
- Customer support and assistance

3. INFORMATION SHARING

3.1 Regulatory Authorities
- UK Financial Conduct Authority (FCA)
- Anti-Money Laundering (AML) authorities
- Tax authorities and law enforcement
- Other regulatory bodies as required by law

3.2 Service Providers
- Banking and payment processors
- Identity verification services
- Cloud hosting and infrastructure providers
- Customer support and analytics services

3.3 Legal Requirements
- Court orders and legal proceedings
- Regulatory investigations
- Fraud prevention and security
- Protection of rights and safety

4. DATA PROTECTION & SECURITY

4.1 Security Measures
- Industry-standard encryption (AES-256)
- Multi-factor authentication
- Secure data centers with 24/7 monitoring
- Regular security audits and penetration testing
- Access controls and authentication protocols

4.2 Data Retention
- Personal data retained for regulatory compliance
- Trading data retained for 7 years minimum
- Account data retained until account closure + 7 years
- Right to data deletion upon request (subject to legal requirements)

4.3 International Transfers
- Data may be transferred to countries with adequate protection
- Standard contractual clauses for international transfers
- Compliance with GDPR and UK data protection laws
- Notification of data breaches within 72 hours

5. YOUR RIGHTS

5.1 Access & Control
- Right to access your personal information
- Right to correct inaccurate data
- Right to request data deletion
- Right to data portability

5.2 Consent Management
- Right to withdraw consent for marketing
- Right to object to data processing
- Right to restrict processing
- Right to lodge complaints with supervisory authorities

5.3 Account Controls
- Update personal information through account settings
- Manage communication preferences
- Control cookie and tracking settings
- Request account closure and data deletion

6. COOKIES & TRACKING

6.1 Essential Cookies
- Session management and authentication
- Security and fraud prevention
- Platform functionality and performance
- User preferences and settings

6.2 Analytics Cookies
- Website usage and performance analysis
- User behavior and experience optimization
- Error tracking and debugging
- Service improvement insights

6.3 Marketing Cookies
- Personalized content and recommendations
- Marketing campaign effectiveness
- Cross-platform advertising
- Social media integration

7. THIRD-PARTY SERVICES

7.1 Payment Processors
- Secure payment processing
- Transaction verification and settlement
- Fraud detection and prevention
- Regulatory compliance

7.2 Identity Verification
- Document verification services
- Biometric authentication
- Address verification
- Risk assessment and screening

7.3 Analytics & Support
- Customer support platforms
- Analytics and monitoring tools
- Communication services
- Security and backup services

8. CHILDREN'S PRIVACY

8.1 Age Requirements
- Services not intended for individuals under 18
- No intentional collection of children's data
- Immediate deletion if child data is discovered
- Parental consent required for users 16-17

9. DATA BREACHES

9.1 Incident Response
- Immediate investigation and containment
- Notification to supervisory authorities within 72 hours
- Notification to affected individuals
- Remediation and prevention measures

9.2 Breach Notification
- Clear and timely communication
- Description of breach and potential impact
- Steps taken to address the breach
- Recommendations for affected individuals

10. CHANGES TO PRIVACY POLICY

10.1 Policy Updates
- Regular review and updates
- Notification of material changes
- Continued use constitutes acceptance
- Right to object to changes

10.2 Version Control
- Clear version numbering and dates
- Archive of previous versions
- Change log and summary
- Effective date notification

11. CONTACT INFORMATION

11.1 Data Protection Officer
- Email: dpo@aircliftcapital.com
- Address: [Company Address]
- Phone: [Contact Number]
- Response time: Within 30 days

11.2 General Inquiries
- Email: privacy@aircliftcapital.com
- Customer support: support@aircliftcapital.com
- Legal inquiries: legal@aircliftcapital.com

12. COMPLIANCE & CERTIFICATION

12.1 Regulatory Compliance
- UK GDPR compliance
- FCA data protection requirements
- ISO 27001 information security
- SOC 2 Type II certification

12.2 Regular Audits
- Annual privacy impact assessments
- Third-party security audits
- Regulatory compliance reviews
- Continuous monitoring and improvement

---

By using AirClift Capital services, you acknowledge that you have read, understood, and agree to this Privacy Policy.`,
    },
    risk: {
      title: "Risk Disclosure Statement",
      content: `AIRCLIFT CAPITAL RISK DISCLOSURE STATEMENT

IMPORTANT: This document contains important information about the risks associated with cryptocurrency and commodities trading. Please read it carefully before proceeding.

1. CRYPTOCURRENCY TRADING RISKS

1.1 Extreme Price Volatility
- Cryptocurrency prices can fluctuate dramatically within minutes
- 24/7 trading with no circuit breakers or trading halts
- Sudden price movements can result in significant losses
- No guarantee of price stability or recovery

1.2 Market Manipulation
- Unregulated markets susceptible to manipulation
- Pump and dump schemes and coordinated trading
- Insider trading and information asymmetry
- Limited regulatory oversight and protection

1.3 Technology Risks
- Blockchain network failures and congestion
- Smart contract vulnerabilities and bugs
- Exchange hacks and security breaches
- Technical glitches and system failures

1.4 Regulatory Uncertainty
- Evolving and inconsistent regulations globally
- Potential bans or restrictions in certain jurisdictions
- Tax implications and reporting requirements
- Compliance costs and legal risks

2. TRADING BOT & ALGORITHMIC RISKS

2.1 Performance Risks
- Past performance does not guarantee future results
- Algorithmic strategies may underperform in certain market conditions
- Technical failures can result in missed opportunities or losses
- Market conditions may render strategies ineffective

2.2 Execution Risks
- Slippage and price impact on large orders
- Network latency and execution delays
- Order routing and execution quality
- Market liquidity constraints

2.3 Strategy Risks
- High-frequency trading risks and costs
- Scalping strategy limitations in volatile markets
- Correlation risks across multiple positions
- Over-optimization and curve fitting risks

3. LEVERAGE & MARGIN RISKS

3.1 Amplified Losses
- Leverage magnifies both gains and losses
- Small price movements can result in significant losses
- Risk of losing more than initial investment
- Margin calls and forced liquidations

3.2 Margin Requirements
- Minimum margin requirements may change
- Additional margin calls during volatile periods
- Risk of account suspension for insufficient margin
- Potential for negative account balances

4. COMMODITIES OPTIONS RISKS

4.1 Options-Specific Risks
- Time decay and expiration risk
- Implied volatility fluctuations
- Limited liquidity in certain options contracts
- Complex pricing models and Greeks

4.2 European-Style Options
- Exercise only at expiration
- No early exercise opportunities
- Time value erosion as expiration approaches
- Potential for worthless expiration

5. PLATFORM & OPERATIONAL RISKS

5.1 Technical Failures
- System outages and maintenance downtime
- Data feed interruptions and delays
- Order execution failures
- Platform accessibility issues

5.2 Cybersecurity Risks
- Unauthorized access to accounts
- Phishing attacks and social engineering
- Malware and ransomware threats
- Data breaches and privacy violations

5.3 Third-Party Dependencies
- Exchange and liquidity provider risks
- Banking and payment processor failures
- Cloud service provider outages
- Regulatory and compliance service failures

6. WITHDRAWAL & LIQUIDITY RISKS

6.1 Lock-In Periods
- 6-month minimum lock-in for retail accounts
- 12-month minimum lock-in for institutional accounts
- Early withdrawal penalties (35% retail, 50% institutional)
- Limited access to funds during lock-in periods

6.2 Liquidity Constraints
- Market conditions may limit withdrawal ability
- Large withdrawals may impact remaining investors
- Processing delays and administrative requirements
- Regulatory restrictions on fund movements

7. PROFIT SHARING & FEE RISKS

7.1 Performance-Based Fees
- 15% company share for retail accounts
- 10% company share for institutional accounts
- Fees only on realized profits
- No guarantee of positive returns

7.2 Fee Structure Risks
- Fee calculations based on net realized profits
- Potential for high fees during profitable periods
- No fee refunds for subsequent losses
- Fee structure may change with notice

8. REGULATORY & COMPLIANCE RISKS

8.1 Regulatory Changes
- Evolving cryptocurrency regulations
- Changes in tax treatment and reporting
- New compliance requirements and costs
- Potential regulatory restrictions or bans

8.2 Jurisdictional Risks
- UK-based operations and regulations
- Cross-border trading restrictions
- Local law variations and conflicts
- Regulatory enforcement actions

9. MARKET & ECONOMIC RISKS

9.1 Global Economic Factors
- Economic recessions and market crashes
- Inflation and currency devaluation
- Interest rate changes and monetary policy
- Geopolitical events and conflicts

9.2 Correlation Risks
- Correlation with traditional markets
- Sector-specific risks and trends
- Global liquidity conditions
- Market sentiment and psychology

10. ADDICTION & PSYCHOLOGICAL RISKS

10.1 Trading Addiction
- Potential for compulsive trading behavior
- Emotional decision-making and FOMO
- Overconfidence and risk-taking
- Financial and personal consequences

10.2 Psychological Factors
- Stress and anxiety from trading losses
- Sleep disruption and health impacts
- Relationship and social consequences
- Professional and career impacts

11. SUITABILITY WARNINGS

11.1 Investment Suitability
- Only suitable for sophisticated investors
- Requires understanding of complex financial instruments
- Not suitable for retirement savings
- Should not represent majority of net worth

11.2 Risk Tolerance Requirements
- High risk tolerance required
- Ability to withstand significant losses
- Long-term investment horizon recommended
- Diversification across asset classes advised

12. DISCLAIMERS & LIMITATIONS

12.1 No Investment Advice
- Platform does not provide investment advice
- All trading decisions are client responsibility
- Consult qualified financial advisors
- Independent research and due diligence required

12.2 No Guarantees
- No guarantee of profits or returns
- No protection against losses
- No guarantee of platform availability
- No guarantee of withdrawal ability

12.3 Limitation of Liability
- Platform liability limited by terms and conditions
- No liability for market losses
- No liability for technical failures
- No liability for regulatory changes

WARNING: CRYPTOCURRENCY TRADING IS HIGHLY SPECULATIVE AND RISKY. ONLY INVEST FUNDS YOU CAN AFFORD TO LOSE COMPLETELY. PAST PERFORMANCE DOES NOT GUARANTEE FUTURE RESULTS.

By proceeding with account registration and trading, you acknowledge that you have read, understood, and accept all risks outlined in this disclosure statement.`,
    },
    bonus: {
      title: "Bonus/Promotions Policy",
      content: `AIRCLIFT CAPITAL BONUS & PROMOTIONS POLICY

1. COMPANY TRADING EQUITY (CTE) OVERVIEW

1.1 Definition
- CTE represents promotional bonus funds credited to trading accounts
- Non-withdrawable bonus capital for trading purposes only
- Profits generated from CTE may be withdrawn subject to terms
- CTE is not real money and cannot be converted to cash

1.2 Purpose
- Incentivize trading activity and platform engagement
- Provide additional capital for strategy testing
- Reward loyal customers and high-volume traders
- Attract new clients to the platform

2. ELIGIBILITY REQUIREMENTS

2.1 Account Requirements
- Valid and verified trading account
- Completed KYC/AML verification
- Compliance with platform terms and conditions
- No history of bonus abuse or violations

2.2 Geographic Restrictions
- Available in eligible jurisdictions only
- Excluded from restricted countries
- Subject to local regulatory requirements
- Compliance with international sanctions

2.3 Account Limitations
- One bonus per person/household
- One bonus per IP address
- No multiple account bonuses
- Corporate accounts may have different terms

3. BONUS TYPES & STRUCTURES

3.1 Welcome Bonuses
- New account registration bonuses
- First deposit matching programs
- Referral bonuses for new clients
- Limited-time promotional offers

3.2 Trading Volume Bonuses
- Volume-based bonus tiers
- Milestone achievement rewards
- Loyalty program benefits
- VIP client exclusive bonuses

3.3 Special Promotions
- Seasonal and holiday promotions
- Event-specific bonus offers
- Partnership and affiliate bonuses
- Institutional client bonuses

4. BONUS TERMS & CONDITIONS

4.1 Wagering Requirements
- Minimum trading volume requirements
- Time limits for bonus utilization
- Specific asset class requirements
- Performance benchmarks to meet

4.2 Withdrawal Restrictions
- CTE funds are non-withdrawable
- Only profits from CTE may be withdrawn
- Minimum profit thresholds may apply
- Withdrawal processing times and fees

4.3 Bonus Expiration
- Time limits for bonus activation
- Expiration dates for unused bonuses
- Extension policies and conditions
- Forfeiture of expired bonuses

5. TRADING REQUIREMENTS

5.1 Minimum Trading Activity
- Required number of trades
- Minimum trade sizes
- Trading frequency requirements
- Asset class diversification

5.2 Performance Requirements
- Minimum profit targets
- Risk management requirements
- Maximum drawdown limits
- Consistency requirements

5.3 Strategy Restrictions
- Prohibited trading strategies
- Scalping and arbitrage limitations
- High-frequency trading restrictions
- Market manipulation prevention

6. ABUSE PREVENTION & MONITORING

6.1 Prohibited Activities
- Multiple account creation
- Bonus hunting and exploitation
- Artificial trading volume
- Collusion and coordinated trading

6.2 Monitoring Systems
- Automated fraud detection
- Manual review processes
- Behavioral analysis
- Pattern recognition algorithms

6.3 Penalties for Violations
- Immediate bonus forfeiture
- Account suspension or termination
- Legal action for fraud
- Reporting to regulatory authorities

7. BONUS CALCULATION & DISTRIBUTION

7.1 Calculation Methods
- Percentage-based bonuses
- Fixed amount bonuses
- Tiered bonus structures
- Performance-based calculations

7.2 Distribution Process
- Automatic credit to accounts
- Manual review and approval
- Processing timeframes
- Notification and confirmation

7.3 Bonus Tracking
- Real-time bonus balance display
- Progress tracking and reporting
- Achievement notifications
- Performance analytics

8. TAX IMPLICATIONS

8.1 Tax Treatment
- Bonuses may be taxable income
- Reporting requirements vary by jurisdiction
- Platform does not provide tax advice
- Client responsibility for tax compliance

8.2 Documentation
- Bonus transaction records
- Tax reporting documentation
- Audit trail maintenance
- Compliance with tax authorities

9. PROMOTIONAL COMMUNICATIONS

9.1 Marketing Materials
- Accurate and truthful advertising
- Clear terms and conditions
- No misleading claims
- Compliance with advertising regulations

9.2 Communication Channels
- Email notifications
- Platform announcements
- SMS alerts (with consent)
- Social media promotions

10. CHANGES & AMENDMENTS

10.1 Policy Updates
- Right to modify bonus terms
- Notification of changes
- Grandfathering of existing bonuses
- Effective date of changes

10.2 Discontinuation
- Right to end promotions
- Notice period for changes
- Impact on existing bonuses
- Alternative offers and compensation

11. DISPUTE RESOLUTION

11.1 Internal Resolution
- Customer support escalation
- Management review process
- Documentation requirements
- Response timeframes

11.2 External Resolution
- Regulatory complaints
- Legal proceedings
- Arbitration options
- Alternative dispute resolution

12. CONTACT & SUPPORT

12.1 Bonus Support
- Dedicated bonus support team
- Email: bonuses@aircliftcapital.com
- Live chat support
- Phone support for VIP clients

12.2 Documentation
- Bonus terms and conditions
- FAQ and help articles
- Video tutorials and guides
- One-on-one assistance

---

By accepting any bonus or promotional offer, you acknowledge that you have read, understood, and agree to this Bonus & Promotions Policy in its entirety.`,
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Row>
        <Col lg={8} className="mx-auto">
          {/* Terms and Conditions */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaFileContract className="text-primary me-2" />
                  <h5 className="mb-0">Terms and Conditions</h5>
                </div>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowModal("terms")}
                    className="me-2"
                  >
                    <FaEye className="me-1" /> View
                  </Button>
                  <Button variant="outline-secondary" size="sm">
                    <FaDownload className="me-1" /> Download
                  </Button>
                </div>
              </div>

              <Form.Check
                type="checkbox"
                id="terms-checkbox"
                checked={disclosures.termsAccepted || false}
                onChange={(e) =>
                  handleCheckboxChange("termsAccepted", e.target.checked)
                }
                label={
                  <span>
                    I have read, understood, and agree to the{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline"
                      onClick={() => setShowModal("terms")}
                    >
                      Terms and Conditions
                    </button>
                  </span>
                }
                className="mb-2"
              />

              {errors.termsAccepted && (
                <Alert variant="danger" className="mt-2 py-2">
                  <FaExclamationTriangle className="me-2" />
                  {errors.termsAccepted}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Privacy Policy */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaShieldAlt className="text-primary me-2" />
                  <h5 className="mb-0">Privacy Policy</h5>
                </div>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowModal("privacy")}
                    className="me-2"
                  >
                    <FaEye className="me-1" /> View
                  </Button>
                  <Button variant="outline-secondary" size="sm">
                    <FaDownload className="me-1" /> Download
                  </Button>
                </div>
              </div>

              <Form.Check
                type="checkbox"
                id="privacy-checkbox"
                checked={disclosures.privacyAccepted || false}
                onChange={(e) =>
                  handleCheckboxChange("privacyAccepted", e.target.checked)
                }
                label={
                  <span>
                    I acknowledge that I have read and understand the{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline"
                      onClick={() => setShowModal("privacy")}
                    >
                      Privacy Policy
                    </button>{" "}
                    and consent to the processing of my personal data
                  </span>
                }
                className="mb-2"
              />

              {errors.privacyAccepted && (
                <Alert variant="danger" className="mt-2 py-2">
                  <FaExclamationTriangle className="me-2" />
                  {errors.privacyAccepted}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Risk Disclosure */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="text-warning me-2" />
                  <h5 className="mb-0">Risk Disclosure Statement</h5>
                </div>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowModal("risk")}
                    className="me-2"
                  >
                    <FaEye className="me-1" /> View
                  </Button>
                  <Button variant="outline-secondary" size="sm">
                    <FaDownload className="me-1" /> Download
                  </Button>
                </div>
              </div>

              <Alert variant="warning" className="mb-3">
                <FaExclamationTriangle className="me-2" />
                <strong>Important:</strong> Trading cryptocurrencies involves
                substantial risk of loss and is not suitable for all investors.
              </Alert>

              <Form.Check
                type="checkbox"
                id="risk-checkbox"
                checked={disclosures.riskDisclosureAccepted || false}
                onChange={(e) =>
                  handleCheckboxChange(
                    "riskDisclosureAccepted",
                    e.target.checked
                  )
                }
                label={
                  <span>
                    I acknowledge that I have read and understood the{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline"
                      onClick={() => setShowModal("risk")}
                    >
                      Risk Disclosure Statement
                    </button>{" "}
                    and understand the risks involved in cryptocurrency trading
                  </span>
                }
                className="mb-2"
              />

              {errors.riskDisclosureAccepted && (
                <Alert variant="danger" className="mt-2 py-2">
                  <FaExclamationTriangle className="me-2" />
                  {errors.riskDisclosureAccepted}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Bonus Policy */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaFileContract className="text-primary me-2" />
                  <h5 className="mb-0">Bonus/Promotions Policy</h5>
                  <span className="badge bg-secondary ms-2">Optional</span>
                </div>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowModal("bonus")}
                    className="me-2"
                  >
                    <FaEye className="me-1" /> View
                  </Button>
                  <Button variant="outline-secondary" size="sm">
                    <FaDownload className="me-1" /> Download
                  </Button>
                </div>
              </div>

              <Form.Check
                type="checkbox"
                id="bonus-checkbox"
                checked={disclosures.bonusPolicyAccepted || false}
                onChange={(e) =>
                  handleCheckboxChange("bonusPolicyAccepted", e.target.checked)
                }
                label={
                  <span>
                    I have read and agree to the{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline"
                      onClick={() => setShowModal("bonus")}
                    >
                      Bonus/Promotions Policy
                    </button>{" "}
                    and wish to be eligible for promotional offers
                  </span>
                }
                className="mb-2"
              />
            </Card.Body>
          </Card>

          {/* Digital Signature */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <FaSignature className="text-primary me-2" />
                <h5 className="mb-0">Digital Signature</h5>
              </div>

              <p className="text-muted mb-3">
                Please type your full legal name below to digitally sign this
                agreement. This constitutes your electronic signature and is
                legally binding.
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Full Legal Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your full legal name"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  onBlur={(e) => handleSignature(e.target.value)}
                  isInvalid={!!errors.signature}
                  className="bg-transparent"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.signature}
                </Form.Control.Feedback>
              </Form.Group>

              {disclosures.digitalSignature && (
                <Alert variant="success" className="mb-0">
                  <FaCheckCircle className="me-2" />
                  Digitally signed by:{" "}
                  <strong>{disclosures.digitalSignature}</strong>
                  <br />
                  <small>
                    Date: {new Date(disclosures.signatureDate).toLocaleString()}
                  </small>
                </Alert>
              )}

              {errors.signature && (
                <Alert variant="danger" className="mt-2 py-2">
                  <FaExclamationTriangle className="me-2" />
                  {errors.signature}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Final Confirmation */}
          {allRequiredAccepted && (
            <Alert variant="success" className="mb-4">
              <FaCheckCircle className="me-2" />
              <strong>All disclosures completed!</strong> You may now proceed to
              the final review.
            </Alert>
          )}
        </Col>
      </Row>

      {/* Document Modal */}
      <Modal
        show={!!showModal}
        onHide={() => setShowModal(null)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{showModal && documents[showModal]?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="text-wrap small">
            {showModal && documents[showModal]?.content}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(null)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const field =
                showModal === "terms"
                  ? "termsAccepted"
                  : showModal === "privacy"
                  ? "privacyAccepted"
                  : showModal === "risk"
                  ? "riskDisclosureAccepted"
                  : "bonusPolicyAccepted";
              handleCheckboxChange(field, true);
              setShowModal(null);
            }}
          >
            Accept & Close
          </Button>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default DisclosuresStep;
